/**
 * Machine journey readiness — cold-start book order for admin Parcours.
 * Pure counts from API; no ATP / price logic here.
 */
import type { CapabilityPack } from '../identity/capability-packs.js';
import { CAPABILITY_PACK } from '../identity/capability-packs.js';

export const MACHINE_JOURNEY_IDS = [
  'people',
  'catalog',
  'locations',
  'buy',
  'receive',
  'release',
  'sell',
  'return',
  'money',
] as const;

export type MachineJourneyId = (typeof MACHINE_JOURNEY_IDS)[number];

export type MachineReadinessSignals = {
  staffAccountCount: number;
  catalogSkuCount: number;
  locationCount: number;
  qualifiedSupplierCount: number;
  purchaseOrderCount: number;
  batchCount: number;
  batchSaleableQty: number;
  shippedOrderCount: number;
  returnAssessmentCount: number;
  lotStopCount: number;
};

/** Pack visibility: user needs at least one listed pack (SUPER_ADMIN bypass in caller). */
export const MACHINE_JOURNEY_REQUIRED_PACKS: Record<MachineJourneyId, readonly CapabilityPack[]> = {
  people: [CAPABILITY_PACK.PARTY_ADMIN],
  catalog: [CAPABILITY_PACK.CATALOG_MAINTAIN],
  locations: [CAPABILITY_PACK.STOCK_MOVE],
  buy: [CAPABILITY_PACK.BUY_PROCURE],
  receive: [CAPABILITY_PACK.STOCK_MOVE],
  release: [CAPABILITY_PACK.STOCK_MOVE],
  sell: [CAPABILITY_PACK.SELL_COMMIT],
  return: [CAPABILITY_PACK.STOCK_MOVE],
  money: [CAPABILITY_PACK.MONEY_READ, CAPABILITY_PACK.MONEY_POST],
};

export function machineStepComplete(id: MachineJourneyId, s: MachineReadinessSignals): boolean {
  switch (id) {
    case 'people':
      return s.staffAccountCount >= 1;
    case 'catalog':
      return s.catalogSkuCount >= 1;
    case 'locations':
      return s.locationCount >= 1;
    case 'buy':
      return s.qualifiedSupplierCount >= 1 && s.purchaseOrderCount >= 1;
    case 'receive':
      return s.batchCount >= 1;
    case 'release':
      return s.batchSaleableQty > 0;
    case 'sell':
      return s.shippedOrderCount >= 1;
    case 'return':
      return s.returnAssessmentCount >= 1 || s.lotStopCount >= 1;
    case 'money':
      return false;
    default:
      return false;
  }
}

export function visibleMachineJourneyIds(
  userPacks: ReadonlySet<CapabilityPack>,
  opts?: { isSuperAdmin?: boolean },
): MachineJourneyId[] {
  if (opts?.isSuperAdmin) return [...MACHINE_JOURNEY_IDS];
  return MACHINE_JOURNEY_IDS.filter((id) => {
    const required = MACHINE_JOURNEY_REQUIRED_PACKS[id];
    return required.some((p) => userPacks.has(p));
  });
}

export function nextIncompleteMachineStep(
  visibleIds: readonly MachineJourneyId[],
  signals: MachineReadinessSignals,
): MachineJourneyId | null {
  for (const id of MACHINE_JOURNEY_IDS) {
    if (!visibleIds.includes(id)) continue;
    if (!machineStepComplete(id, signals)) return id;
  }
  return null;
}

export function machineJourneyProgress(
  visibleIds: readonly MachineJourneyId[],
  signals: MachineReadinessSignals,
): { complete: MachineJourneyId[]; incomplete: MachineJourneyId[] } {
  const complete: MachineJourneyId[] = [];
  const incomplete: MachineJourneyId[] = [];
  for (const id of visibleIds) {
    if (machineStepComplete(id, signals)) complete.push(id);
    else incomplete.push(id);
  }
  return { complete, incomplete };
}

/** Upstream machine steps that must be complete before acting on this pack (requirements I/O order). */
export const MACHINE_JOURNEY_PREREQUISITES: Record<MachineJourneyId, readonly MachineJourneyId[]> = {
  people: [],
  catalog: [],
  locations: ['catalog'],
  buy: ['catalog'],
  receive: ['catalog', 'locations', 'buy'],
  release: ['receive'],
  sell: ['release'],
  return: ['sell'],
  money: [],
};

/** Requirements subsystem slug per journey pack. */
export const MACHINE_JOURNEY_SUBSYSTEM: Record<MachineJourneyId, string> = {
  people: 'authority-audit',
  catalog: 'catalog-identity',
  locations: 'stock-lot',
  buy: 'procurement',
  receive: 'stock-lot',
  release: 'stock-lot',
  sell: 'sell-fulfill',
  return: 'quality-recall',
  money: 'money-close',
};

/** Role-shaped entry slice (seeded identity.Role.name). SUPER_ADMIN uses full machine order. */
export const ROLE_PRIMARY_JOURNEYS: Record<string, readonly MachineJourneyId[]> = {
  SUPER_ADMIN: MACHINE_JOURNEY_IDS,
  WAREHOUSE: ['locations', 'receive', 'release', 'return'],
  BUYER: ['buy'],
  SALES: ['sell'],
  CATALOG: ['catalog'],
  ACCOUNTANT: ['money'],
  VIEWER: [],
};

export function roleEntryPath(
  roleNames: readonly string[],
  visibleIds: readonly MachineJourneyId[],
): MachineJourneyId[] {
  if (roleNames.includes('SUPER_ADMIN')) {
    return MACHINE_JOURNEY_IDS.filter((id) => visibleIds.includes(id));
  }
  const primary = new Set<MachineJourneyId>();
  for (const role of roleNames) {
    const packs = ROLE_PRIMARY_JOURNEYS[role];
    if (!packs) continue;
    for (const id of packs) primary.add(id);
  }
  if (primary.size === 0) return [];
  return MACHINE_JOURNEY_IDS.filter((id) => primary.has(id) && visibleIds.includes(id));
}

export function packPrerequisitesMet(
  packId: MachineJourneyId,
  signals: MachineReadinessSignals,
): boolean {
  if (packId === 'money') return true;
  return MACHINE_JOURNEY_PREREQUISITES[packId].every((p) => machineStepComplete(p, signals));
}

/** First unmet upstream pack id, or null when clear (money never blocks). */
export function packBlockedReason(
  packId: MachineJourneyId,
  signals: MachineReadinessSignals,
): MachineJourneyId | null {
  if (packId === 'money') return null;
  for (const p of MACHINE_JOURNEY_PREREQUISITES[packId]) {
    if (!machineStepComplete(p, signals)) return p;
  }
  return null;
}

/** First incomplete pack in role entry slice, else global next incomplete among visible. */
export function defaultActivePack(
  roleNames: readonly string[],
  visibleIds: readonly MachineJourneyId[],
  signals: MachineReadinessSignals,
): MachineJourneyId | null {
  const entry = roleEntryPath(roleNames, visibleIds);
  const searchIds = entry.length > 0 ? entry : visibleIds;
  for (const id of searchIds) {
    if (!machineStepComplete(id, signals)) return id;
  }
  return nextIncompleteMachineStep(visibleIds, signals);
}
